import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import OptimizedImage from "@/components/OptimizedImage";
import HeroSocialProof from "@/components/HeroSocialProof";
import { useLanguage } from "@/contexts/LanguageContext";

// i18n helper for aria-labels (no visible text — screen readers only).
const _pick = (obj, lang) => obj[lang] || obj.en || obj.fr || "";
const _t = {
  prev: { en: "Previous slide", fr: "Diapositive précédente", es: "Diapositiva anterior" },
  next: { en: "Next slide", fr: "Diapositive suivante", es: "Diapositiva siguiente" },
  slide: {
    en: (n) => `Slide ${n}`,
    fr: (n) => `Diapositive ${n}`,
    es: (n) => `Diapositiva ${n}`,
  },
  bgVideo: { en: "background video", fr: "vidéo d'arrière-plan", es: "vídeo de fondo" },
};

// Hero slider — cinematic, narrative-aware.
// Each slide gets a slow Ken-Burns zoom while active.
// Image leads (1300 ms easeOut), text follows (~700 ms with 180 ms delay) for a layered, premium feel.
// Slides only advance via user interaction (arrows, dots, keyboard) — no autoplay.
//
// API:
//   <HeroSlider images={[...]} />  // legacy: image-only background, fills parent (absolute inset-0)
//   <HeroSlider slides={[{image, overline, title, titleAccent, sub, ctaTo, ctaLabel, secondaryTo, secondaryLabel, badge}]} />
//
// Narrative mode is *self-sizing*: the slider lays out all narratives stacked
// in a single CSS-grid cell so the section grows to fit the tallest narrative,
// guaranteeing nothing is clipped on any viewport. A `min-h-[100svh]` baseline
// keeps the cinematic full-screen feel on landscape displays.
//
// `scope` (optional) opts every slide image into the stable CMS slot-id
// scheme — when set, the root <div> declares `data-cms-scope={scope}` and
// each <OptimizedImage> declares `data-cms-role="slide.<i>.image"` (poster
// gets `slide.<i>.poster`). The AutoImageEditor uses this to produce slot
// ids like `cms.<scope>.slide.0.image`, identical across environments so
// "Mirror DB" reliably propagates Production edits to Preview.
const HeroSlider = ({
  images = [],
  slides = [],
  alt = "Grup Xaluca",
  interval = 7000,
  withNarrative = false,
  minHeightClass = "min-h-[100svh]",
  scope,
}) => {
  const items = useMemo(() => {
    if (slides && slides.length) return slides.filter(Boolean);
    return (images || []).filter(Boolean).map((src) => ({ image: src }));
  }, [images, slides]);

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 → next, -1 → previous
  const { lang } = useLanguage();

  // Track which slide indexes have ever been active. Slides only mount
  // their <picture> markup once visited, so a 12-slide hero doesn't
  // download all 12 image variants on first paint. The first slide
  // (i === 0) is always mounted as it's the LCP element.
  const [visitedIndexes, setVisitedIndexes] = useState(() => new Set([0]));
  useEffect(() => {
    setVisitedIndexes((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, [index]);

  // Track whether we're on a mobile viewport so we can swap any
  // YouTube-iframe video backgrounds out for the static poster image —
  // mobile autoplay restrictions + bandwidth concerns make the iframe a
  // poor fit there.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener ? mq.addEventListener("change", sync) : mq.addListener(sync);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", sync) : mq.removeListener(sync);
    };
  }, []);

  const next = useCallback(
    (dir = 1) => {
      setDirection(dir);
      setIndex((i) => ((i + dir) % items.length + items.length) % items.length);
    },
    [items.length]
  );
  const go = useCallback(
    (target) => {
      setDirection(target > index ? 1 : -1);
      setIndex(((target % items.length) + items.length) % items.length);
    },
    [index, items.length]
  );

  // Keyboard arrow navigation — fallback for desktop users without a mouse.
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") next(-1);
      else if (e.key === "ArrowRight") next(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length, next]);

  if (items.length === 0) return null;
  const showNarrative = withNarrative || slides.length > 0;

  // Root layout differs by mode:
  //  - Narrative mode → self-sizing (relative) so the section adapts to content
  //  - Image-only mode → background filler (absolute inset-0)
  const rootClass = showNarrative
    ? `relative w-full overflow-hidden ${minHeightClass}`
    : "absolute inset-0 overflow-hidden";

  return (
    <div
      className={rootClass}
      data-testid="hero-slider"
      data-cms-scope={scope || undefined}
    >
      {/* Layered images with Ken-Burns on the active one */}
      {items.map((s, i) => {
        const isActive = i === index;
        const isVisited = visitedIndexes.has(i);
        // Detect direct video files vs embed iframes. Anything that ends in
        // a common video extension or comes from stream.mux.com is played
        // inline via HTML5 <video>; everything else stays an iframe.
        const isDirectVideo =
          !!s.video &&
          (/\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(s.video) ||
            /stream\.mux\.com/i.test(s.video));
        // Video background policy:
        //   • Direct video files (mp4/webm/Mux) → play on ALL devices
        //     (mobile browsers have supported muted + playsInline autoplay
        //     since iOS 10 / Android 5, so there's no reason to fall back
        //     to a static poster there).
        //   • Iframe embeds (YouTube/Vimeo) → still play only above the
        //     mobile breakpoint because those platforms have unreliable
        //     autoplay policies on mobile and often intercept taps into a
        //     fullscreen player, which would break the hero UX.
        //   • The `image` field always acts as the graceful poster fallback
        //     everywhere the video can't play.
        const useVideo = !!s.video && (isDirectVideo || !isMobile);
        // Video slides use a HARD CUT transition (no cross-fade) so the
        // video never visually blends with the next/previous still-image
        // slide. Per user feedback: the video slide must show ONLY the
        // video, with no image superimposed at any time — including
        // during slider auto-rotation. Other slides keep their cinematic
        // 1.3 s cross-fade between each other.
        const transitionClass = useVideo
          ? "transition-none"
          : "transition-opacity duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)]";
        return (
          <div
            key={(s.image || s.video || "") + i}
            className={`absolute inset-0 ${transitionClass} ${
              isActive ? "opacity-100 z-[1]" : "opacity-0 z-0"
            }`}
            data-testid={`hero-slide-${i}`}
            aria-hidden={!isActive}
          >
            {!isVisited ? (
              // Unvisited slide: empty container (preserves the
              // cinematic fade target without fetching the image).
              null
            ) : useVideo ? (
              <>
                {/* Video-only slide: the <video> element itself paints
                    its `poster` while loading, so no separate <img>
                    layer is needed underneath (per user feedback —
                    the first slide must contain ONLY the video). */}
                {/* Video-only slide: the <video>'s own native `poster`
                    attribute acts as a graceful fallback that the browser
                    only paints while the file is loading OR if playback
                    fails (Mux outage, blocked autoplay, codec mismatch).
                    Once the video starts playing, the poster vanishes
                    automatically — so the slide still shows "only the
                    video" while it can, and never a separate <img> layer. */}
                {isDirectVideo ? (
                  <video
                    src={s.video}
                    poster={s.videoPoster || s.image}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                    data-testid={`hero-slide-${i}-video`}
                  />
                ) : (
                  <iframe
                    src={s.video}
                    title={`${alt} ${_pick(_t.bgVideo, lang)}`}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none border-0 min-w-[177.77vh] min-h-[56.25vw] w-[177.77vh] h-[56.25vw]"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen={false}
                    loading="lazy"
                    data-testid={`hero-slide-${i}-video`}
                  />
                )}
              </>
            ) : (
              <OptimizedImage
                src={s.image}
                alt={`${alt} ${i + 1}`}
                priority={i === 0}
                sizes="100vw"
                className={`absolute inset-0 w-full h-full object-cover will-change-transform ${
                  isActive ? `hero-kenburns hero-kenburns-${i % 4}` : ""
                }`}
                objectFit="cover"
                data-cms-role={scope ? `slide.${i}.image` : undefined}
              />
            )}
          </div>
        );
      })}

      {/* Base readability scrim — applied to EVERY slide (image, video,
          narrative or plain). Subtle uniform dark tint that protects text
          contrast on bright desert, dune and sky backgrounds without
          burying the photography. Sits over images/videos (z-[1]) but
          under the narrative gradient (also z-[2]) and the text (z-[3]).  */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none bg-[#1A1513]/30"
        data-testid="hero-readability-scrim"
        aria-hidden="true"
      />

      {/* Cinematic gradient + soft vignette for legibility (narrative mode) */}
      {showNarrative && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/55 to-[#1A1513]/20 z-[2] pointer-events-none" />
          <div
            className="absolute inset-0 z-[2] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(26,21,19,0.55) 100%)" }}
          />
        </>
      )}

      {/* Film grain overlay — 35mm editorial feel, paired with Ken-Burns */}
      <span className="film-grain !z-[2]" aria-hidden="true" />

      {/* Site-wide brand monogram anchored to the right edge of the hero */}
      <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="hero-slider-brand" />

      {/* Narrative — stacked CSS-grid cell. All slides share the same grid area
          so the container grows to fit the *tallest* narrative; no clipping. */}
      {showNarrative && (
        <div className="relative z-[3] w-full flex items-end pt-32 md:pt-40 pb-24 md:pb-32">
          <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12 text-[#FDFBF7]">
            <div className="hero-narrative-grid">
              {items.map((s, i) => {
                const isActive = i === index;
                const offset = isActive
                  ? "translate-x-0"
                  : direction === 1
                  ? "-translate-x-6"
                  : "translate-x-6";
                return (
                  <div
                    key={`narrative-${i}`}
                    className={`hero-narrative-cell max-w-3xl will-change-transform transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isActive ? "opacity-100 blur-0 pointer-events-auto" : "opacity-0 blur-[2px] pointer-events-none"
                    } ${offset}`}
                    style={{ transitionDelay: isActive ? "180ms" : "0ms" }}
                    data-testid={`hero-narrative-${i}`}
                    aria-hidden={!isActive}
                  >
                    {s.badge && (
                      <span className="inline-block mb-5 text-[10px] tracking-[0.35em] uppercase px-3 py-1.5 border border-[#D4A373]/60 text-[#D4A373] rounded-full bg-[#1A1513]/35 backdrop-blur-sm">
                        {s.badge}
                      </span>
                    )}
                    {s.overline && (
                      <p
                        className="text-[11px] tracking-[0.35em] uppercase text-[#D4A373]"
                        data-testid={`hero-overline-${i}`}
                      >
                        {s.overline}
                      </p>
                    )}
                    {s.title && (
                      <h1
                        className="font-serif-x mt-5 text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tighter"
                        data-testid={`hero-title-${i}`}
                      >
                        {/* Title is split into discrete <span> leaves so
                            each fragment becomes an independent slot in
                            edit mode. Without these spans the H1 would
                            be disqualified as a leaf (nested <em>) and
                            the bare text node would have no element to
                            bind to — making the title un-editable. */}
                        <span data-testid={`hero-title-main-${i}`}>{s.title}</span>
                        {s.titleAccent && (
                          <>
                            <br />
                            <em className="not-italic text-[#D4A373]" data-testid={`hero-title-accent-${i}`}>{s.titleAccent}</em>
                          </>
                        )}
                      </h1>
                    )}
                    {s.sub && (
                      <p
                        className="mt-7 text-base md:text-xl text-[#FDFBF7]/85 max-w-2xl leading-relaxed"
                        data-testid={`hero-sub-${i}`}
                      >
                        {s.sub}
                      </p>
                    )}
                    {s.socialProof && (
                      <HeroSocialProof
                        {...s.socialProof}
                        scope={s.socialProof.scope || `hero.slide-${i}.social-proof`}
                      />
                    )}
                    {/* Optional slot for custom in-narrative CTAs (e.g. a
                        channel picker on /contact). Renders directly
                        beneath the description so the buttons stay part
                        of the hero content rather than floating at the
                        bottom of the section. */}
                    {s.extraCtas ? (
                      <div className="mt-8 md:mt-9 max-w-3xl" data-testid={`hero-extra-ctas-${i}`}>
                        {s.extraCtas}
                      </div>
                    ) : null}
                    {(s.ctaTo || s.secondaryTo || s.onSecondaryClick) && (
                      <div className="mt-9 flex flex-wrap gap-4">
                        {s.ctaTo && (
                          <Link
                            to={s.ctaTo}
                            className="bg-[#C16542] text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#A35133] transition-colors duration-300 inline-flex items-center gap-2"
                            data-testid={`hero-cta-primary-${i}`}
                          >
                            <span data-testid={`hero-cta-primary-label-${i}`}>{s.ctaLabel}</span>
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                          </Link>
                        )}
                        {s.secondaryTo && (
                          <Link
                            to={s.secondaryTo}
                            className="border border-[#FDFBF7]/40 text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors duration-300"
                            data-testid={`hero-cta-secondary-${i}`}
                          >
                            <span data-testid={`hero-cta-secondary-label-${i}`}>{s.secondaryLabel}</span>
                          </Link>
                        )}
                        {!s.secondaryTo && s.onSecondaryClick && (
                          // Button variant of the secondary CTA — used when
                          // the CTA opens an in-page modal (e.g. the "Tu
                          // viaje empieza con una conversación" info
                          // modal on /booking) instead of navigating to
                          // an anchor / route. Keeps the visual language
                          // identical to the Link variant above.
                          <button
                            type="button"
                            onClick={s.onSecondaryClick}
                            className="border border-[#FDFBF7]/40 text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors duration-300"
                            data-testid={`hero-cta-secondary-${i}`}
                          >
                            <span data-testid={`hero-cta-secondary-label-${i}`}>{s.secondaryLabel}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => next(-1)}
            className="flex absolute left-3 sm:left-4 md:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 items-center justify-center bg-[#1A1513]/45 hover:bg-[#1A1513]/70 text-[#FDFBF7] backdrop-blur-sm transition-colors rounded-full"
            aria-label={_pick(_t.prev, lang)}
            data-testid="hero-slider-prev"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => next(1)}
            className="flex absolute right-3 sm:right-4 md:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 items-center justify-center bg-[#1A1513]/45 hover:bg-[#1A1513]/70 text-[#FDFBF7] backdrop-blur-sm transition-colors rounded-full"
            aria-label={_pick(_t.next, lang)}
            data-testid="hero-slider-next"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
          </button>

          {/* Dots + slide counter — no auto-advance, plain active/idle pills. */}
          <div
            className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3"
            data-testid="hero-slider-dots"
          >
            {items.map((_, i) => (
              <button
                key={`dot-${i}`}
                type="button"
                onClick={() => go(i)}
                aria-label={_t.slide[lang] ? _t.slide[lang](i + 1) : `Slide ${i + 1}`}
                aria-current={i === index}
                className={`h-[3px] w-10 rounded-full transition-colors duration-300 ${
                  i === index ? "bg-[#D4A373]" : "bg-[#FDFBF7]/30 hover:bg-[#FDFBF7]/55"
                }`}
                data-testid={`hero-slider-dot-${i}`}
              />
            ))}
            <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/70 hidden sm:inline tabular-nums">
              {String(index + 1).padStart(2, "0")}
              <span className="mx-1 text-[#FDFBF7]/40">/</span>
              {String(items.length).padStart(2, "0")}
            </span>
          </div>
        </>
      )}

      {/* Component-local CSS */}
      <style>{`
        /* CSS-grid stacking: every narrative slide occupies the same single cell
           so the container's natural height becomes max(content heights). */
        .hero-narrative-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          grid-template-rows: auto;
        }
        .hero-narrative-cell {
          grid-column: 1 / 2;
          grid-row: 1 / 2;
        }
        @keyframes hero-kenburns-0 {
          0%   { transform: scale(1.02) translate3d(0, 0, 0); }
          100% { transform: scale(1.10) translate3d(-1.2%, -1.0%, 0); }
        }
        @keyframes hero-kenburns-1 {
          0%   { transform: scale(1.02) translate3d(0, 0, 0); }
          100% { transform: scale(1.10) translate3d(1.2%, -0.6%, 0); }
        }
        @keyframes hero-kenburns-2 {
          0%   { transform: scale(1.10) translate3d(-0.8%, 0.6%, 0); }
          100% { transform: scale(1.02) translate3d(0.6%, 0, 0); }
        }
        @keyframes hero-kenburns-3 {
          0%   { transform: scale(1.04) translate3d(0.8%, -0.6%, 0); }
          100% { transform: scale(1.12) translate3d(-1.0%, 0.8%, 0); }
        }
        .hero-kenburns { animation-duration: ${interval + 1500}ms; animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1); animation-fill-mode: forwards; }
        .hero-kenburns-0 { animation-name: hero-kenburns-0; }
        .hero-kenburns-1 { animation-name: hero-kenburns-1; }
        .hero-kenburns-2 { animation-name: hero-kenburns-2; }
        .hero-kenburns-3 { animation-name: hero-kenburns-3; }
        @media (prefers-reduced-motion: reduce) {
          .hero-kenburns { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default HeroSlider;
